import { useEffect, useRef, useCallback } from 'react';
import { AnalyticsService, AnalyticsTrackingEvent } from '../../analytics';
import { tryGetVisitorId } from '../../analytics/fingerprint';
import {
  WidgetImpressionProperties,
  WidgetSessionStartProperties,
  ConciergeWidgetType,
  getDeviceType,
  getPageNameFromPath,
  getUTMParams,
} from '../tracking';

const SESSION_TRACKED_KEY = 'concierge_session_tracked';

interface UseWidgetVisibilityTrackingOptions {
  /** The app name from the brand config */
  appName?: string;
  /** BrandApp V2 app.appId, emitted as the analytics appId. */
  appId?: string;
  /** The type of widget being displayed */
  widgetType?: ConciergeWidgetType;
  /** Whether tracking is enabled (e.g., only track when widget is rendered) */
  enabled?: boolean;
  /** Reactive page identifier (e.g., pathname) -- resets impression tracking on change so
   *  long-lived components (like the floating button) fire per-page rather than per-mount. */
  pageKey?: string;
}

/**
 * Hook to track WIDGET_IMPRESSION and WIDGET_SESSION_START events for the concierge.
 *
 * - WIDGET_IMPRESSION: Fires when the widget becomes visible (uses IntersectionObserver)
 * - WIDGET_SESSION_START: Fires ONCE per session when user first sees the widget
 *
 * @returns A ref callback to attach to the widget element for visibility tracking
 */
export function useWidgetVisibilityTracking(options: UseWidgetVisibilityTrackingOptions = {}) {
  const { appName = '', appId, widgetType = 'floating_button', enabled = true, pageKey } = options;

  // Track whether we've already fired impression for this component instance
  const hasTrackedImpression = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementRef = useRef<HTMLElement | null>(null);

  /**
   * Check if session start has already been tracked in this browser session
   */
  const isSessionTracked = useCallback((): boolean => {
    if (typeof window === 'undefined') return true;
    try {
      return sessionStorage.getItem(SESSION_TRACKED_KEY) === 'true';
    } catch {
      // sessionStorage might not be available (e.g., incognito mode in some browsers)
      return false;
    }
  }, []);

  /**
   * Mark session as tracked
   */
  const markSessionTracked = useCallback((): void => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(SESSION_TRACKED_KEY, 'true');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  }, []);

  /**
   * Track WIDGET_IMPRESSION event
   * Non-blocking - fire and forget
   */
  const trackWidgetImpression = useCallback(async () => {
    if (!enabled || hasTrackedImpression.current) return;
    hasTrackedImpression.current = true;

    const sessionId = (await tryGetVisitorId()) || 'unknown';

    const properties: WidgetImpressionProperties = {
      pageUrl: window.location.href,
      pageName: getPageNameFromPath(window.location.pathname),
      widgetType,
      sessionId,
      appName: appName || 'unknown',
      appVersion: 'v2',
      ...(appId && { appId }),
    };

    // Non-blocking - don't await
    AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_IMPRESSION, properties).catch((err) => {
      // Silently handle tracking errors - don't break the app
      console.debug('[Concierge Tracking] Failed to track widget impression:', err);
    });
  }, [enabled, widgetType, appName, appId]);

  /**
   * Track WIDGET_SESSION_START event (once per session)
   * Non-blocking - fire and forget
   */
  const trackWidgetSessionStart = useCallback(async () => {
    if (!enabled || isSessionTracked()) return;

    // Mark as tracked immediately to prevent race conditions
    markSessionTracked();

    const sessionId = (await tryGetVisitorId()) || 'unknown';
    const utmParams = getUTMParams();

    const properties: WidgetSessionStartProperties = {
      sessionId,
      appName: appName || 'unknown',
      referrer: document.referrer || '',
      deviceType: getDeviceType(),
      pageUrl: window.location.href,
      appVersion: 'v2',
      ...utmParams,
      ...(appId && { appId }),
    };

    // Non-blocking - don't await
    AnalyticsService.track(AnalyticsTrackingEvent.CONCIERGE_WIDGET_SESSION_START, properties).catch((err) => {
      // Silently handle tracking errors - don't break the app
      console.debug('[Concierge Tracking] Failed to track widget session start:', err);
    });
  }, [enabled, appName, appId, isSessionTracked, markSessionTracked]);

  /**
   * Set up IntersectionObserver to track when widget becomes visible
   */
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          // Widget is visible - track both events
          trackWidgetImpression();
          trackWidgetSessionStart();

          // Disconnect observer after firing - we only need to track once per page load
          observerRef.current?.disconnect();
        }
      },
      {
        // Fire when at least 50% of the element is visible
        threshold: 0.5,
      }
    );

    // Observe the element if it's already set
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [enabled, trackWidgetImpression, trackWidgetSessionStart]);

  /**
   * Reset impression tracking when the page changes (SPA navigation).
   * Long-lived components like the floating button stay mounted across routes,
   * so pageKey (typically location.pathname) lets us fire per-page instead of per-mount.
   */
  const pageKeyRef = useRef(pageKey);
  useEffect(() => {
    if (!enabled || pageKey === undefined || pageKey === pageKeyRef.current) return;
    pageKeyRef.current = pageKey;
    hasTrackedImpression.current = false;
    trackWidgetImpression();
  }, [pageKey, enabled, trackWidgetImpression]);

  /**
   * Ref callback to attach to the widget element
   * This handles both initial render and subsequent updates
   */
  const setTrackingRef = useCallback((element: HTMLElement | null) => {
    elementRef.current = element;

    if (element && observerRef.current) {
      observerRef.current.observe(element);
    }
  }, []);

  /**
   * Manual trigger for tracking (useful for components that don't use the ref)
   * Tracks impression immediately without waiting for intersection
   */
  const trackImmediate = useCallback(() => {
    trackWidgetImpression();
    trackWidgetSessionStart();
  }, [trackWidgetImpression, trackWidgetSessionStart]);

  return {
    /** Ref callback to attach to the widget element for visibility-based tracking */
    setTrackingRef,
    /** Manually trigger tracking immediately (bypasses IntersectionObserver) */
    trackImmediate,
  };
}
